import React, { memo, useMemo, useEffect, useState, useCallback } from 'react';
import cn from 'classnames';
import { Tab } from '@headlessui/react';
import { useRouter } from 'next/router';

import { noop } from 'lodash';
import { Image, Spinner } from 'components';
import Confirmation from 'components/Confirmation';
import Modal from 'components/Modal';
import { ButtonV3 } from 'components/ComponentV2';
import ActionMenu from 'components/ActionMenu';
import { Row } from 'components/layout';
import { Button } from 'components/form';
import Calendar from 'components/Calendar';
import { addCohostModalInstance } from 'components/AddCohostModal';
import {
  ConfirmationScreen,
  SocietyTabsType,
  eventColumns,
} from 'hooks/useCampaigns.hook';
import { useNotification } from 'hooks/useNotification';
import { useEvent } from 'hooks/useEvent';
import { useCohost } from 'hooks/useCohost';
import { useCommon } from 'hooks/useCommon';
import { PAGE_ROUTES } from 'lib/page-routes';
import { TouchpointType } from 'lib/models/touchpoint';
import { useCampaigns } from 'lib/contexts/campaigns-context';
import { TouchpointState } from 'lib/models/opportunity';
import { parseResponse } from 'lib/utils/parser';
import { useSocietiesSearch } from 'lib/contexts/societies-search-context';
import { CohostResponse } from 'lib/models/cohost';
import { SocietyDashboardAttributes } from 'lib/models/student-society';
import { getRatingAverage } from 'utils/format';
import { getOpportunityPublicPagePathname } from 'utils/urls';
import styles from './SocietyEventsTable.module.scss';

export const EventStateOption = [
  {
    id: '1',
    value: 'all',
    label: 'All',
  },
  {
    id: '2',
    value: 'published',
    label: 'Live',
  },
  {
    id: '3',
    value: 'draft',
    label: 'Drafts',
  },
  {
    id: '4',
    value: 'invited',
    label: 'Invited',
  },
  {
    id: '5',
    value: 'expired',
    label: 'Ended',
  },
];

export interface SocietyEventsTableProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  societyDashboard: SocietyDashboardAttributes;
  mutateSocietyDashboard: () => void;
}

export const SocietyEventsTable: React.FC<SocietyEventsTableProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    societyDashboard,
    mutateSocietyDashboard,
  }: SocietyEventsTableProps) => {
    const contentClassNames = cn(
      {
        [styles.SocietyEventsTable]: true,
      },
      className
    );
    const notificationInstance = useNotification();
    const router = useRouter();
    const endOfPageRef = React.useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
      endOfPageRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const [touchpoint, setTouchpoint] = useState<{
      id: string;
      title: string;
      state: string;
    } | null>(null);
    const [isDeleteButtonLoading, setIsDeleteButtonLoading] = useState(false);

    const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
      useState(false);
    const [confirmationScreenSelected, setConfirmationScreenSelected] =
      useState<ConfirmationScreen>(ConfirmationScreen.NO_SCREEN);

    const { setSearch } = useSocietiesSearch();

    const { addCohost } = useCohost();

    const { event_status_count_mapping } = societyDashboard || {};

    const { eventStateChangeHandler, getState, shouldPublishEvent } =
      useEvent();
    const {
      eventsList,
      size,
      setSize,
      hasMoreRecords,
      isLoadingMore,
      totalCount,
      selectedEventState,
      setSelectedEventState,
      isAPILoading,
      onDelete,
      mutateEvents,
    } = useCampaigns();

    const { onShare } = useCommon();

    const onUnpublishEventHandler = useCallback(async () => {
      try {
        if (touchpoint) {
          const response = await eventStateChangeHandler({
            state: touchpoint.state as TouchpointState,
            id: touchpoint.id as string,
            unpublish: true,
          });
          if (response) {
            mutateEvents();
            mutateSocietyDashboard();
            setIsConfirmationModalOpen(false);
            setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
            setTouchpoint(null);
            notificationInstance.success({
              title: 'Event Unpublished!',
              message: `${touchpoint.title} has been unpublished successfully.`,
            });
          }
        }
      } catch (error) {
        console.error(error);
        notificationInstance.handleExceptionError(error);
      }
    }, [touchpoint]);

    const deleteConfirmationScreenDesc = touchpoint ? (
      <div>
        <p>
          Are you sure you want to delete{' '}
          <span style={{ fontWeight: 700 }}>{touchpoint.title}</span>?
        </p>
        <br />
        <p>All of the data and details associated with it will be gone.</p>
        <br />
        {touchpoint.state === TouchpointState.EXPIRED ? null : (
          <p>
            <button
              style={{
                background: 'none',
                color: 'blue',
                border: 'none',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '15px',
              }}
              onClick={() => onUnpublishEventHandler()}
            >
              Unpublish
            </button>{' '}
            instead if you want to keep the data.
          </p>
        )}
      </div>
    ) : null;

    const unpublishConfirmationScreenDesc = touchpoint ? (
      <p>
        Are you sure you want to unpublish event{' '}
        <span style={{ fontWeight: 700 }}>{touchpoint.title}</span>?
      </p>
    ) : null;

    const ConfirmationScreenProps = [
      {
        id: ConfirmationScreen.DELETE,
        title: 'Delete Event',
        desc: deleteConfirmationScreenDesc,
        buttonContent: 'Yes, Delete',
        onCancel: () => {
          setIsConfirmationModalOpen(false);
          setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
        },
        onConfirm: async () => {
          if (touchpoint) {
            setIsDeleteButtonLoading(true);
            const response = await onDelete(touchpoint.id as string);
            if (response) {
              mutateSocietyDashboard();
              setIsConfirmationModalOpen(false);
              setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
              setTouchpoint(null);
            }
            setIsDeleteButtonLoading(false);
          }
        },
      },
      {
        id: ConfirmationScreen.UNPUBLISH,
        title: 'Unpublish Event',
        desc: unpublishConfirmationScreenDesc,
        buttonContent: 'Yes, Unpublish',
        onCancel: () => {
          setIsConfirmationModalOpen(false);
          setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
          setTouchpoint(null);
        },
        onConfirm: async () => {
          await onUnpublishEventHandler();
        },
      },
    ];

    const getConfirmationModal = () => {
      const screen = ConfirmationScreenProps.find(
        (item) => item.id === confirmationScreenSelected
      );
      if (screen) {
        const { title, desc, buttonContent, onCancel, onConfirm } = screen;
        return (
          <Modal
            isOpen={isConfirmationModalOpen}
            onRequestClose={() => setIsConfirmationModalOpen(false)}
            isFullScreen={false}
          >
            <Confirmation
              title={title}
              desc={desc || ''}
              buttonContent={buttonContent}
              onCancel={onCancel}
              isLoading={isDeleteButtonLoading}
              onConfirm={onConfirm}
            />
          </Modal>
        );
      }
    };

    const getActionMenu = (
      id: string,
      title: string,
      publicUrl: string,
      state: string,
      societyId: string,
      cohost_application: CohostResponse | null | undefined
    ) => {
      return [
        {
          id: '1',
          label:
            cohost_application === null && state !== TouchpointState.EXPIRED
              ? 'Edit'
              : null,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            const pathname = PAGE_ROUTES.EVENTS_EDIT.replace('[id]', id);
            router.push(`${pathname}?step=1&type=Event`);
          },
        },
        {
          id: '2',
          label: 'Share',
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            onShare(title, publicUrl);
          },
        },
        {
          id: '3',
          label: 'Preview',
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            window.open(publicUrl, '_blank');
          },
        },
        {
          id: '4',
          label:
            cohost_application === null && state !== TouchpointState.EXPIRED
              ? 'Add Co-host'
              : null,
          onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            try {
              const cohostIds = await addCohostModalInstance({
                idsForCohost: { creatableForId: societyId, touchpointId: id },
              });
              if (cohostIds.length > 0) {
                for (let i = 0; i < cohostIds.length; i++) {
                  const response = await addCohost(id, cohostIds[i]);
                  if (!response) {
                    break;
                  } else {
                    notificationInstance.success({
                      title: 'Success!',
                      message: `Cohost/s added successfully.`,
                    });
                  }
                }
              }
              setSearch('');
            } catch (error) {
              noop;
            }
          },
        },
        {
          id: '5',
          label:
            cohost_application === null && state !== TouchpointState.EXPIRED
              ? state === TouchpointState.DRAFT
                ? 'Publish'
                : 'Unpublish'
              : null,
          onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            setTouchpoint({ id, title, state });
            if (state !== TouchpointState.DRAFT) {
              setIsConfirmationModalOpen(true);
              setConfirmationScreenSelected(ConfirmationScreen.UNPUBLISH);
            } else {
              const isEventValid = await shouldPublishEvent(id as string);
              if (!isEventValid) {
                const pathname = PAGE_ROUTES.EVENTS_EDIT.replace('[id]', id);
                notificationInstance.warning({
                  title: 'Required',
                  message: `Fill required information to publish event.`,
                });
                router.push(`${pathname}?step=1&type=Event`);
              } else {
                try {
                  const response = await eventStateChangeHandler({
                    state: state as TouchpointState,
                    id: id as string,
                  });
                  if (response) {
                    mutateEvents();
                    notificationInstance.success({
                      title: 'Event Published!',
                      message: `${title} has been published successfully.`,
                    });
                  }
                } catch (error) {
                  console.error(error);
                  notificationInstance.handleExceptionError(error);
                }
              }
            }
          },
        },
        {
          id: '6',
          label: cohost_application === null ? 'Delete' : null,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            setTouchpoint({ id, title, state });
            setIsConfirmationModalOpen(true);
            setConfirmationScreenSelected(ConfirmationScreen.DELETE);
          },
        },
      ];
    };

    const eventData = useMemo(() => {
      if (!isAPILoading && eventsList && eventsList.length === 0) {
        return (
          <div className={styles.noResults}>
            <div className={styles.noResultsContent}>
              <div className={styles.noResultsContentText}>
                <Image src="desert.png" source="aws" height="auto" width="98" />
                <p>Post your job or internship</p>
              </div>
              <ButtonV3
                onClick={() =>
                  router.push(`${PAGE_ROUTES.CAMPAIGNS_CREATE}?step=1`)
                }
              >
                Create new event
              </ButtonV3>
            </div>
          </div>
        );
      } else {
        return (
          <table className={cn({ [styles.loading]: isLoadingMore })}>
            <thead>
              <tr>
                {eventColumns.map((column) => (
                  <th key={column.id} style={{ width: column.width }}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {eventsList.map((row) => {
                const {
                  id,
                  title,
                  start_date,
                  applications,
                  state,
                  slug,
                  view_counter,
                  creatable_for,
                  cohost_application,
                  is_online,
                  online_link,
                  place: placeResponse,
                  rating_map,
                } = row;
                const place = parseResponse(placeResponse);
                const ratingAverage = getRatingAverage(rating_map);
                const { id: societyId } = parseResponse(creatable_for);
                const publicPageUrl = `${
                  window.location.origin
                }${getOpportunityPublicPagePathname(
                  TouchpointType.Event,
                  slug
                )}`;

                return (
                  <tr
                    key={id}
                    className={cn({
                      [styles.invitationPending]:
                        getState(
                          state as TouchpointState,
                          cohost_application
                        ) === TouchpointState.INVITATION_PENDING,
                    })}
                    onClick={() =>
                      router.push(
                        {
                          pathname: `${PAGE_ROUTES.CAMPAIGNS}/${id}`,
                          query: { selectedEventState },
                        },
                        `${PAGE_ROUTES.CAMPAIGNS}/${id}`
                      )
                    }
                  >
                    <td>
                      {start_date && (
                        <Calendar
                          startDate={start_date}
                          className={cn({
                            [styles.expired]: state === TouchpointState.EXPIRED,
                          })}
                        />
                      )}
                    </td>
                    <td>
                      <div className={cn(styles.title, styles.titleSection)}>
                        <p>{title}</p>
                        {is_online && !online_link ? (
                          <p className={styles.titleSubSection}>
                            Meeting link missing
                          </p>
                        ) : null}
                        {!is_online && !place ? (
                          <p className={styles.titleSubSection}>
                            Location missing
                          </p>
                        ) : null}
                        {ratingAverage > 0 ? (
                          <p className={styles.rating}>⭐️ {ratingAverage}</p>
                        ) : null}
                      </div>
                    </td>
                    <td className={styles.state}>
                      {getState(state as TouchpointState, cohost_application)}
                    </td>
                    <td>{view_counter}</td>
                    <td>{applications}</td>
                    <td>
                      <ActionMenu
                        options={getActionMenu(
                          id,
                          title,
                          publicPageUrl,
                          state,
                          societyId,
                          cohost_application
                        )}
                        triggerButtonClassName={styles.actionmenu}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      }
    }, [selectedEventState, eventsList]);

    useEffect(() => {
      if (!isLoadingMore && size > 1) {
        scrollToBottom();
      }
    }, [isLoadingMore, size]);

    useEffect(() => {
      setSize(1);
      if (router.query && router.query.selectedEventState !== undefined) {
        const index = Number(router.query.selectedEventState);
        setSelectedEventState(index);
      }

      return () => {
        endOfPageRef.current = null;
        setSelectedEventState(SocietyTabsType.ALL);
      };
    }, []);

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="SocietyEventsTable"
      >
        <Tab.Group
          selectedIndex={selectedEventState}
          onChange={(index) => {
            setSelectedEventState(index);
            setSize(1);
          }}
        >
          <Tab.List className={styles.tabList}>
            {EventStateOption.map((tab) => {
              const isValuePresent = event_status_count_mapping
                ? event_status_count_mapping[
                    tab.value as keyof typeof event_status_count_mapping
                  ]
                : false;
              return (
                <Tab
                  key={tab.id}
                  className={({ selected }) =>
                    cn(
                      styles.tab,
                      { [styles.tabSelected]: selected },
                      {
                        [styles.tabDisabled]: !isValuePresent && tab.id !== '1',
                      }
                    )
                  }
                >
                  {tab.label}
                </Tab>
              );
            })}
          </Tab.List>
          <Tab.Panels className={styles.tabPanels}>
            <Tab.Panel>{eventData}</Tab.Panel>
            <Tab.Panel>{eventData}</Tab.Panel>
            <Tab.Panel>{eventData}</Tab.Panel>
            <Tab.Panel>{eventData}</Tab.Panel>
            <Tab.Panel>{eventData}</Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
        {isLoadingMore ? (
          <Spinner size="small" className={styles.spinner} />
        ) : null}
        <Row align="center" className={styles.countsRow}>
          {eventsList?.length ? (
            <p>
              Showing {eventsList?.length} of {totalCount}
            </p>
          ) : null}
          {hasMoreRecords ? (
            <Button
              variant="text"
              onClick={() => {
                setSize(size + 1);
              }}
              className={styles.countsButton}
            >
              Show more
            </Button>
          ) : null}
          <div ref={endOfPageRef}></div>
        </Row>
        {isConfirmationModalOpen ? getConfirmationModal() : null}
      </div>
    );
  }
);

SocietyEventsTable.displayName = 'SocietyEventsTable';
